import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardHeader, EmptyState, PageHeader, StatCard, StatGrid } from '@/design/components'
import { api } from '@/lib/api'
import { EMPTY, formatDateLong, plural } from '@/lib/format'
import { dashboardWidgets } from '@/modules/registry'
import { useMe } from './MeContextProvider'
import { ActivityCard, AiDocumentsCard, DeadlinesCard, MyCasesCard } from './dashboard/widgets'

/**
 * Pulpit — układ 1:1 z design/screens/design-main.png.
 *
 * DASHBOARD-DATA (podłączone): liczniki KPI pochodzą z endpointów `stats` modułów
 * (`/api/cases/stats`, `/api/documents/inbox/stats`, `/api/deadlines/stats`), a strumień
 * zdarzeń z rdzeniowego `/api/me/activity`. Każde zapytanie jest włączane dopiero, gdy
 * `/api/me/context` zwrócił dany moduł — dzięki temu wyłączony moduł nie generuje 403/404,
 * a jego kafel pokazuje „—" z wyjaśnieniem w stopce.
 *
 * Listy („Dokumenty rozpoznane przez AI", „Najbliższe terminy", „Moje sprawy") dostarczają
 * moduły przez `FrontendModule.dashboardWidgets`; sekcje poniżej to fallback, gdy widgetu brak.
 */
/** Miejscownik — „GODZINY W SIERPNIU" jak w mockupie. */
const MONTHS_LOCATIVE = [
  'styczniu',
  'lutym',
  'marcu',
  'kwietniu',
  'maju',
  'czerwcu',
  'lipcu',
  'sierpniu',
  'wrześniu',
  'październiku',
  'listopadzie',
  'grudniu',
]

/** 8550 minut → „142,5" — godziny dziesiętnie po polsku (kafel „Godziny w …"). */
function formatHoursPl(minutes: number): string {
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 1 }).format(minutes / 60)
}

interface CaseStats {
  total: number
  in_progress: number
  awaiting: number
  suspended: number
  closed: number
  advisory: number
}

interface InboxStats {
  today: number
  to_verify: number
  low_confidence: number
  decreed: number
  total: number
}

interface DeadlineStats {
  next7: number
  critical: number
  //: Terminy po upływie daty. Liczone OSOBNO, a nie doliczane do `next7` —
  //: inaczej kafel mówił „7 w ciągu 7 dni", a lista obok pokazywała jeden.
  overdue: number
}

interface TimesheetStats {
  month_minutes: number
  month_billable_minutes: number
  today_minutes: number
}

/** Wiersz z `/api/admin/modules` — czytany tylko przez administratora (patrz `moduleStates`). */
interface AdminModuleState {
  key: string
  name: string
  enabled: boolean
}

interface ActivityEntry {
  id: number
  user_initials: string | null
  text: string
  when: string
}

export function Dashboard() {
  const { can, modules, moduleKeys, navItems } = useMe()
  const navigate = useNavigate()

  const firstRouteOf = (moduleKey: string): string | undefined =>
    navItems.find((item) => item.moduleKey === moduleKey)?.route

  const documentsRoute = firstRouteOf('documents')
  const casesRoute = firstRouteOf('cases')
  const deadlinesRoute = firstRouteOf('deadlines')
  const timesheetRoute = firstRouteOf('timesheet')

  const hasCases = moduleKeys.includes('cases')
  const hasDocuments = moduleKeys.includes('documents')
  const hasDeadlines = moduleKeys.includes('deadlines')
  const hasTimesheet = moduleKeys.includes('timesheet')

  const caseStats = useQuery({
    queryKey: ['dashboard', 'cases', 'stats'],
    queryFn: () => api.get<CaseStats>('/cases/stats'),
    enabled: hasCases,
  })
  const inboxStats = useQuery({
    queryKey: ['dashboard', 'documents', 'inbox-stats'],
    queryFn: () => api.get<InboxStats>('/documents/inbox/stats'),
    enabled: hasDocuments,
  })
  const deadlineStats = useQuery({
    queryKey: ['dashboard', 'deadlines', 'stats'],
    queryFn: () => api.get<DeadlineStats>('/deadlines/stats'),
    enabled: hasDeadlines,
  })
  const timesheetStats = useQuery({
    queryKey: ['dashboard', 'timesheet', 'stats'],
    queryFn: () => api.get<TimesheetStats>('/timesheet/stats'),
    enabled: hasTimesheet,
  })
  // Rozróżnienie „moduł wyłączony w instalacji" od „moduł ukryty dla Twojej roli" wymaga
  // stanu z `MODULES_ENABLED`, którego `/api/me/context` nie niesie (widać w nim tylko moduły
  // widoczne). Zna go endpoint panelu admina — pytamy o niego wyłącznie, gdy user ma do niego
  // prawo; dla pozostałych ról komunikat pozostaje neutralny („brak dostępu").
  const moduleStates = useQuery({
    queryKey: ['dashboard', 'admin', 'modules'],
    queryFn: () => api.get<AdminModuleState[]>('/admin/modules'),
    enabled: can('admin.modules'),
    staleTime: 5 * 60_000,
  })

  /** `true` — moduł działa w instalacji, `false` — wyłączony, `undefined` — nie wiadomo. */
  const installedState = (key: string): boolean | undefined =>
    moduleStates.data?.find((item) => item.key === key)?.enabled

  /** Powód, dla którego moduł nie jest widoczny na pulpicie — bez zgadywania. */
  const unavailableReason = (key: string, name: string): string => {
    const enabled = installedState(key)
    if (enabled === false) return `moduł ${name} wyłączony w tej instalacji`
    if (enabled === true) return `moduł ${name} niewidoczny dla Twojej roli`
    return `brak dostępu do modułu ${name}`
  }

  const activity = useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: () => api.get<ActivityEntry[]>('/me/activity', { query: { limit: 10 } }),
  })

  const mainWidgets = useMemo(() => dashboardWidgets(moduleKeys, 'main'), [moduleKeys])
  const sideWidgets = useMemo(() => dashboardWidgets(moduleKeys, 'side'), [moduleKeys])

  const monthLabel = MONTHS_LOCATIVE[new Date().getMonth()] ?? ''

  const toVerify = inboxStats.data?.to_verify
  const subtitle = [
    formatDateLong(new Date()),
    toVerify !== undefined && toVerify > 0
      ? `${toVerify} ${plural(toVerify, 'dokument czeka', 'dokumenty czekają', 'dokumentów czeka')} na weryfikację`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <PageHeader
        title="Pulpit"
        subtitle={subtitle}
        actions={
          <>
            {/* Akcja pojawia się dopiero, gdy user MOŻE ją wykonać — sama trasa nie wystarcza
                (np. Administrator widzi rejestr spraw, ale ma `cases.create` = nie). */}
            {casesRoute && can('cases.create') ? (
              // Query param `nowa=1` otwiera modal „Nowa sprawa" w module cases.
              <Button variant="secondary" onClick={() => navigate('/sprawy?nowa=1')}>
                Nowa sprawa
              </Button>
            ) : null}
            {documentsRoute && can('documents.inbox') ? (
              // Query param `upload=1` otwiera modal „Prześlij skan" w skrzynce wpływu.
              <Button variant="primary" onClick={() => navigate('/dokumenty/skrzynka?upload=1')}>
                Prześlij dokument
              </Button>
            ) : null}
          </>
        }
      />

      {/* --- 4 kafle KPI (design-main.png) --- */}
      <StatGrid className="mb-5">
        <StatCard
          label="Sprawy w toku"
          value={hasCases ? (caseStats.data?.in_progress ?? EMPTY) : EMPTY}
          unit={hasCases ? 'spraw' : undefined}
          loading={caseStats.isPending && hasCases}
          onClick={casesRoute ? () => navigate(casesRoute) : undefined}
          footer={
            hasCases
              ? caseStats.data
                ? `${caseStats.data.awaiting} oczekuje na termin`
                : caseStats.isError
                  ? 'brak dostępu do rejestru spraw'
                  : 'wczytywanie…'
              : unavailableReason('cases', 'Sprawy')
          }
        />
        <StatCard
          label="Dokumenty do weryfikacji"
          value={hasDocuments ? (inboxStats.data?.to_verify ?? EMPTY) : EMPTY}
          unit={inboxStats.data ? `z ${inboxStats.data.total} wpływu` : undefined}
          loading={inboxStats.isPending && hasDocuments}
          onClick={documentsRoute ? () => navigate(documentsRoute) : undefined}
          footer={
            hasDocuments
              ? inboxStats.data
                ? inboxStats.data.low_confidence > 0
                  ? `${inboxStats.data.low_confidence} ${plural(inboxStats.data.low_confidence, 'pismo', 'pisma', 'pism')} z niską pewnością`
                  : 'wszystkie powyżej progu pewności'
                : inboxStats.isError
                  ? 'brak dostępu do skrzynki wpływu'
                  : 'wczytywanie…'
              : unavailableReason('documents', 'Obieg dokumentów')
          }
        />
        <StatCard
          label="Terminy w ciągu 7 dni"
          value={hasDeadlines ? (deadlineStats.data?.next7 ?? EMPTY) : EMPTY}
          unit={hasDeadlines ? 'terminów' : undefined}
          loading={deadlineStats.isPending && hasDeadlines}
          onClick={deadlinesRoute ? () => navigate(deadlinesRoute) : undefined}
          // Zaległość bije pierwszeństwem nad „krytyczne": termin, który już minął,
          // jest pilniejszy niż taki, do którego zostały trzy dni.
          accent={deadlineStats.data && deadlineStats.data.overdue > 0 ? 'danger' : 'none'}
          footer={
            hasDeadlines
              ? deadlineStats.data
                ? deadlineStats.data.overdue > 0
                  ? `${deadlineStats.data.overdue} ${plural(deadlineStats.data.overdue, 'zaległy', 'zaległe', 'zaległych')} — po terminie`
                  : `${deadlineStats.data.critical} ${plural(deadlineStats.data.critical, 'krytyczny', 'krytyczne', 'krytycznych')} — poniżej 3 dni`
                : deadlineStats.isError
                  ? 'brak dostępu do terminów'
                  : 'wczytywanie…'
              : unavailableReason('deadlines', 'Terminy procesowe')
          }
        />
        <StatCard
          label={`Godziny w ${monthLabel}`}
          value={
            hasTimesheet
              ? timesheetStats.data
                ? formatHoursPl(timesheetStats.data.month_minutes)
                : EMPTY
              : EMPTY
          }
          unit={hasTimesheet && timesheetStats.data ? 'godz.' : undefined}
          loading={timesheetStats.isPending && hasTimesheet}
          onClick={timesheetRoute ? () => navigate(timesheetRoute) : undefined}
          footer={
            hasTimesheet
              ? timesheetStats.data
                ? `${formatHoursPl(timesheetStats.data.month_billable_minutes)} godz. rozliczalne`
                : timesheetStats.isError
                  ? 'brak dostępu do rejestracji czasu'
                  : 'wczytywanie…'
              : unavailableReason('timesheet', 'Rejestracja czasu')
          }
        />
      </StatGrid>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-5">
          {/* Fallback: pusta lista, gdy moduł jest widoczny, ale nie dał widgetu;
              karta „bez dostępu", gdy modułu w ogóle nie ma w kontekście. */}
          {mainWidgets.some((widget) => widget.id.startsWith('documents.')) ? null : hasDocuments ? (
            <AiDocumentsCard items={[]} moreHref={documentsRoute} />
          ) : (
            <UnavailableCard
              title="Dokumenty rozpoznane przez AI — do weryfikacji"
              reason={unavailableReason('documents', 'Obieg dokumentów')}
            />
          )}
          {mainWidgets.map((widget) => (
            <widget.component key={widget.id} />
          ))}
          <ActivityCard
            loading={activity.isPending}
            items={(activity.data ?? []).map((entry) => ({
              id: entry.id,
              initials: entry.user_initials ?? 'AI',
              text: entry.text,
              at: entry.when,
            }))}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          {/* Fallback, gdy moduł deadlines nie dostarcza widgetu. */}
          {sideWidgets.some((widget) => widget.id.startsWith('deadlines.')) ? null : hasDeadlines ? (
            <DeadlinesCard items={[]} moreHref={deadlinesRoute} />
          ) : (
            <UnavailableCard
              title="Najbliższe terminy"
              reason={unavailableReason('deadlines', 'Terminy procesowe')}
            />
          )}
          {/* Fallback, gdy moduł cases nie dostarcza widgetu `cases.*`. */}
          {sideWidgets.some((widget) => widget.id.startsWith('cases.')) ? null : hasCases ? (
            <MyCasesCard items={[]} moreHref={casesRoute} />
          ) : (
            <UnavailableCard title="Moje sprawy" reason={unavailableReason('cases', 'Sprawy')} />
          )}
          {sideWidgets.map((widget) => (
            <widget.component key={widget.id} />
          ))}
        </div>
      </div>

      {modules.length === 0 ? (
        <p className="mt-6 text-small text-muted">
          Żaden moduł nie jest widoczny dla Twojej roli — skontaktuj się z administratorem.
        </p>
      ) : null}
    </>
  )
}

/**
 * Kafel listy, do której zalogowany user nie ma dostępu — świadomie NIE udajemy pustej listy
 * („Brak nadchodzących terminów" sugerowałoby, że terminów nie ma).
 */
function UnavailableCard({ title, reason }: { title: string; reason: string }) {
  return (
    <Card>
      <CardHeader title={title} />
      <EmptyState
        title="Brak dostępu"
        description={`${reason.charAt(0).toUpperCase()}${reason.slice(1)}. Skontaktuj się z administratorem, jeśli potrzebujesz tych danych.`}
      />
    </Card>
  )
}
